import React, { useCallback, useMemo } from 'react';

import Typography from '@mui/material/Typography';
import { useLocation } from 'wouter';

import { decryptName } from '../../common/util/util';
import { AuthContext } from '../../contexts/AuthContext';
import { useParentStyleCriteria } from '../../contexts/ParentStyleContext';
import useBreadcrumb from '../../hooks/useBreadcrumb';

// static breadcrumb label lookup (cases that just return a fixed string)
const PATH_NAME_MAP = {
  Home: 'Home',
  Companies: 'Companies',
  Contracts: 'Contracts',
  Billing: 'Billing',
  Members: 'Members overview',
  Consultations: 'Consultations overview',
  LogRecord: 'Log record',
  EditRecord: 'Edit record',
  ViewRecord: 'View record',
  'Add-contract': 'New contract',
  'Add-company': 'New company',
  BenefitsPlan: 'Benefits Plan',
  BenefitPlan: 'Benefits Plan',
  Eligibletreatment: 'Eligible treatment',
  Taxation: 'Taxation',
  HeadcountHistory: 'Headcount history',
  Entities: 'Entities',
  Budget: 'Budget',
  Claims: 'Claims overview',
  'Claims-tool': 'Claims tool',
  Wallets: 'Wallets overview',
  'Wallet-transactions': 'Wallet Transactions',
  View: 'View',
  Settings: 'Settings',
  'Service-providers': 'Service Providers',
  'Invoice-training': 'Invoice training',
  'Ai-pipelines': 'AI pipelines',
  Resources: 'Navigator',
};

// to add navigation on breadcrumbs
const getBreadcrumbUrl = (path, showAllPaths) => {
  const getLocalStorageItem = (key) => localStorage.getItem(key);
  const contractsUrl = `/contracts/${getLocalStorageItem('contractName')}`;
  const companiesUrl = `/companies/${getLocalStorageItem('companyName')}`;
  const billingUrl = `/billing/${getLocalStorageItem('contractName')}`;

  const urlMap = {
    Contracts: {
      Contracts: '/contracts',
      BenefitPlan: `${contractsUrl}/benefitPlan?plan=${getLocalStorageItem('planType')}&benefitPlanId=${getLocalStorageItem('benefitPlanId')}`,
      default: `${contractsUrl}?id=${getLocalStorageItem('contractId')}`,
    },
    Companies: {
      Companies: '/companies',
      BenefitPlan: `${companiesUrl}/benefitPlan?plan=${getLocalStorageItem('planType')}&benefitPlanId=${getLocalStorageItem('benefitPlanId')}`,
      default: `${companiesUrl}?id=${getLocalStorageItem('companyId')}`,
    },
    Billing: {
      Billing: '/billing',
      default: `${billingUrl}?id=${getLocalStorageItem('contractId') === 'undefined' ? '' : getLocalStorageItem('contractId')}`,
    },
    Members: {
      Members: '/members',
    },
    Claims: {
      Claims: '/claims',
    },
    Wallets: {
      Wallets: '/wallets',
    },
    Transactions: {
      Transactions: '/wallet-transactions',
    },
    Consultations: {
      Consultations: '/consultations',
      Past: '/consultations/past',
      default: '/consultations',
    },
  };

  const key = [
    'Contracts',
    'Companies',
    'Billing',
    'Members',
    'Consultations',
    'Claims',
    'Wallets',
    'Transactions',
  ].find((k) => showAllPaths.includes(k));

  if (key === 'Consultations') {
    return path === 'Past' ? urlMap[key].Past : urlMap[key].Consultations;
  }

  return key ? urlMap[key][path] || urlMap[key].default : null;
};

const Breadcrumbs = () => {
  // Keep route-derived breadcrumbs in sync while the layout stays mounted.
  useLocation();
  const { isCrawOnly } = React.useContext(AuthContext);
  const { activePath, showAllPaths } = useBreadcrumb();
  const { padding } = useParentStyleCriteria();

  const getPathName = useCallback(
    (path) => {
      if (path === 'Employeeeligibility') {
        return isCrawOnly()
          ? 'Employee eligibility'
          : 'Reimbursement configurations';
      }
      if (path in PATH_NAME_MAP) {
        return PATH_NAME_MAP[path];
      }
      return decryptName(decodeURIComponent(path));
    },
    [isCrawOnly],
  );

  // Derive the breadcrumb list without mutating state during render.
  // Mirrors the original logic that dropped the element at index 2.
  const breadcrumbPaths = useMemo(() => {
    const paths = [...(showAllPaths ?? [])];
    if (paths.includes('Consultations') && paths.length > 3) {
      paths.splice(2, 1);
    } else if (paths.includes('Past') && paths.length === 3) {
      paths.splice(2, 1);
    }
    return paths;
  }, [showAllPaths]);

  return (
    <Typography
      paddingLeft={padding === '0px' ? '20px' : '0px'}
      paddingTop={padding === '0px' ? '40px' : '20px'}
    >
      {breadcrumbPaths.map((path, index) => {
        const completePath = window.location.pathname;
        const isPastConsultation = completePath.includes('past');
        const breadcrumbUrl =
          path === 'Consultations' && isPastConsultation
            ? '/consultations/past'
            : getBreadcrumbUrl(path, showAllPaths);

        const isWalletTransactionViewLabel =
          path === 'View' &&
          completePath.includes('/wallet-transactions/view/');

        const crumbStyle =
          activePath === path
            ? { color: '#2D3E91', textDecoration: 'none' }
            : { color: '#403F3F', textDecoration: 'none' };

        const crumbText = (
          <>
            {getPathName(path)}{' '}
            {index !== breadcrumbPaths.length - 1 && '/'}{' '}
          </>
        );

        if (isWalletTransactionViewLabel) {
          return (
            <span
              key={index}
              style={{
                ...crumbStyle,
                cursor: 'default',
                userSelect: 'none',
              }}
            >
              {crumbText}
            </span>
          );
        }

        return (
          <a
            key={index}
            style={crumbStyle}
            href={
              path === 'Home'
                ? '/'
                : activePath !== path
                  ? breadcrumbUrl
                  : undefined
            }
          >
            {crumbText}
          </a>
        );
      })}
    </Typography>
  );
};

export default Breadcrumbs;
